import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  ArrowLeft, Pencil, FilePdf, Trash, Copy, Eye, X,
  Package, Tag, TagSimple, ShareNetwork, LinkSimple, Printer, LockOpen
} from "@phosphor-icons/react";

const REPORT_TYPE_LABELS = {
  lashing: "Cargo Securing Survey Report",
  labeling: "Labeling Report",
  label_removal: "Label Removal Report",
  damaged_shipment: "Damaged Shipment Report",
  transloading: "Transloading Photo Report",
};

const STATUS_STYLES = {
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  finalized: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function FieldRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-1 py-2 border-b border-slate-100">
      <dt className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</dt>
      <dd className="md:col-span-2 text-sm text-slate-900">{value}</dd>
    </div>
  );
}

export default function ReportViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [photoUrls, setPhotoUrls] = useState({});
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reports/${id}`)
      .then(({ data }) => { setReport(data); setLoading(false); })
      .catch(() => { toast.error("Failed to load report"); navigate("/"); });
  }, [id, navigate]);

  // Load photo blobs
  useEffect(() => {
    if (!report) return;
    const allPhotos = [...(report.photos || [])];
    (report.photo_entries || []).forEach(e => { if (e.id) allPhotos.push(e.id); });
    (report.containers || []).forEach(c => allPhotos.push(...(c.photos || [])));
    const unique = [...new Set(allPhotos)];

    unique.forEach(async (pid) => {
      try {
        const resp = await axios.get(`${API}/photos/${pid}`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        setPhotoUrls(prev => ({ ...prev, [pid]: url }));
      } catch {}
    });
  }, [report]);

  const getShareUrl = () => {
    if (!report?.share_token) return null;
    const base = window.location.origin;
    return `${base}/share/${report.share_token}`;
  };

  const handleCopyLink = () => {
    const url = getShareUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Customer link copied to clipboard");
    }
  };

  const handleReopen = async () => {
    if (!window.confirm("Reopen this report for editing?")) return;
    try {
      await axios.post(`${API}/reports/${id}/reopen`, {});
      toast.success("Report reopened");
      window.location.reload();
    } catch { toast.error("Failed to reopen"); }
  };

  const handlePrint = () => { window.print(); };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/reports/${id}/pdf`, {
        responseType: "blob"
      });
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${report?.report_number || "report"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handlePreviewPdf = async () => {
    setPreviewLoading(true);
    setShowPdfPreview(true);
    try {
      const resp = await axios.get(`${API}/reports/${id}/pdf`, {
        responseType: "blob"
      });
      const url = URL.createObjectURL(resp.data);
      setPdfPreviewUrl(url);
    } catch {
      toast.error("Failed to generate PDF preview");
      setShowPdfPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePdfPreview = () => {
    setShowPdfPreview(false);
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  const handleDuplicate = async () => {
    if (!window.confirm("Duplicate this report? A new draft will be created with the same data (dates and photos reset).")) return;
    setDuplicating(true);
    try {
      const { data } = await axios.post(`${API}/reports/${id}/duplicate`, {});
      toast.success(`Report duplicated as ${data.report_number}`);
      navigate(`/reports/${data.id}/edit`);
    } catch {
      toast.error("Failed to duplicate report");
    } finally {
      setDuplicating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this report?")) return;
    try {
      await axios.delete(`${API}/reports/${id}`);
      toast.success("Report deleted");
      navigate("/");
    } catch {
      toast.error("Failed to delete report");
    }
  };

  if (loading || !report) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-blue-900 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const reportType = report.report_type;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="report-view-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Top */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-to-dashboard-btn">
              <ArrowLeft size={18} />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {report.report_number}
                </h1>
                <Badge variant="outline" className={`text-xs capitalize ${STATUS_STYLES[report.status] || ""}`}>
                  {report.status}
                </Badge>
              </div>
              <p className="text-sm text-slate-500">{REPORT_TYPE_LABELS[reportType]}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="view-actions">
            {user?.role !== "viewer" && report.status !== "finalized" && (
              <Button variant="outline" onClick={() => navigate(`/reports/${id}/edit`)} className="gap-2" data-testid="edit-report-btn">
                <Pencil size={16} /> Edit
              </Button>
            )}
            {user?.role === "admin" && report.status === "finalized" && (
              <Button variant="outline" onClick={handleReopen} className="gap-2" data-testid="reopen-report-btn">
                <LockOpen size={16} /> Reopen
              </Button>
            )}
            {user?.role !== "viewer" && (
              <Button variant="outline" onClick={handleDuplicate} disabled={duplicating} className="gap-2" data-testid="duplicate-report-btn">
                <Copy size={16} /> {duplicating ? "..." : "Duplicate"}
              </Button>
            )}
            {report.status === "finalized" && report.share_token && (
              <Button variant="outline" onClick={handleCopyLink} className="gap-2 text-blue-700" data-testid="copy-link-btn">
                <LinkSimple size={16} /> Copy Customer Link
              </Button>
            )}
            <Button variant="outline" onClick={handlePrint} className="gap-2" data-testid="print-btn">
              <Printer size={16} /> Print
            </Button>
            <Button variant="outline" onClick={handlePreviewPdf} className="gap-2" data-testid="preview-pdf-btn">
              <Eye size={16} /> Preview PDF
            </Button>
            <Button onClick={handleDownloadPdf} disabled={downloading} className="bg-blue-900 hover:bg-blue-800 text-white gap-2" data-testid="download-pdf-btn">
              <FilePdf size={16} /> {downloading ? "Generating..." : "Download PDF"}
            </Button>
            {user?.role === "admin" && (
              <Button variant="outline" onClick={handleDelete} className="text-red-600 hover:bg-red-50 gap-2" data-testid="delete-report-btn">
                <Trash size={16} /> Delete
              </Button>
            )}
          </div>
        </div>

        {/* Report content */}
        <div className="space-y-6 fade-in">
          {/* Header Info */}
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>General Information</h3>
            <div className="space-y-0">
              <FieldRow label="Report Number" value={report.report_number} />
              <FieldRow label="Report Type" value={REPORT_TYPE_LABELS[reportType]} />
              <FieldRow label="Applicant" value={report.applicant} />
              <FieldRow label="Reference Number" value={report.reference_number} />
              <FieldRow label="Date of Issuance" value={report.date_of_issuance} />
              <FieldRow label="Surveyor" value={report.surveyor} />
              <FieldRow label="Created By" value={report.created_by_name} />
            </div>
          </div>

          {/* Lashing-specific */}
          {reportType === "lashing" && (
            <>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Shipment Details</h3>
                <FieldRow label="Date of Inspection" value={report.date_of_inspection} />
                <FieldRow label="Place of Inspection" value={report.place_of_inspection} />
                <FieldRow label="Shipper" value={report.shipper} />
                <FieldRow label="Notify Party" value={report.notify_party} />
                <FieldRow label="Port of Loading" value={report.port_of_loading} />
                <FieldRow label="Place of Delivery" value={report.place_of_delivery} />
                <FieldRow label="Commodity Summary" value={report.commodity_summary} />
                <FieldRow label="Total Gross Weight" value={report.total_gross_weight} />
              </div>

              {/* Containers */}
              {(report.containers || []).map((c, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-md p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Container {i + 1}: {c.container_number || "N/A"}
                  </h3>
                  <FieldRow label="Container Type" value={c.container_type} />
                  <FieldRow label="Commodity" value={c.commodity_description} />
                  <FieldRow label="Gross Weight" value={c.gross_weight} />
                  <FieldRow label="Serial Number" value={c.serial_number} />
                  <FieldRow label="Seal Number" value={c.seal_number} />
                  <FieldRow label="TIR Cord Seal" value={c.tir_cord_seal_number} />
                  <FieldRow label="Over-height" value={c.over_height} />
                  <FieldRow label="Cargo Condition" value={c.cargo_condition} />
                  <FieldRow label="Container Condition" value={c.container_condition} />
                  <FieldRow label="Securing Method" value={c.securing_method_description} />
                  <FieldRow label="Securing Methods" value={(c.securing_methods || []).map(m => m.replace(/_/g, ' ')).join(', ')} />
                  <FieldRow label="Lashing Details" value={c.lashing_details} />
                  <FieldRow label="Blocking Details" value={c.blocking_details} />
                  <FieldRow label="Observations" value={c.observations} />
                  {/* Container photos */}
                  {(c.photos || []).length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">Container Photos</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {c.photos.map(pid => (
                          <div key={pid} className="aspect-video bg-slate-100 rounded overflow-hidden border border-slate-200">
                            {photoUrls[pid] ? (
                              <img src={photoUrls[pid]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">Loading...</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Findings */}
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Findings & Observations</h3>
                <FieldRow label="Survey Findings" value={report.survey_findings} />
                <FieldRow label="Observations" value={report.observations} />
                <FieldRow label="Disclaimer" value={report.disclaimer} />
              </div>
            </>
          )}

          {/* Labeling / Label Removal */}
          {(reportType === "labeling" || reportType === "label_removal") && (
            <>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Service Details</h3>
                <FieldRow label="Container ID" value={report.container_id} />
                <FieldRow label="Rail Car Number" value={report.rail_car_number} />
                <FieldRow label="Cargo" value={report.cargo} />
                <FieldRow label="Service" value={report.service} />
                <FieldRow label="Site of Service" value={report.site_of_service} />
                <FieldRow label="Location" value={report.location} />
                <FieldRow label="Date of Nomination" value={report.date_of_nomination} />
                <FieldRow label="Date of Service" value={report.date_of_service} />
                <FieldRow label="Completion Time" value={report.completion_time} />
              </div>

              {/* Removal Table */}
              {(report.removal_table || []).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-md p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Removal Table</h3>
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">CTU</th>
                        <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Class Code</th>
                        <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.removal_table.map((r, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-3 py-2 text-sm">{r.ctu}</td>
                          <td className="px-3 py-2 text-sm">{r.class_code}</td>
                          <td className="px-3 py-2 text-sm">{r.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Affixing Table */}
              {(report.affixing_table || []).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-md p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Affixing Table</h3>
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">CTU</th>
                        <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Class Code</th>
                        <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.affixing_table.map((r, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-3 py-2 text-sm">{r.ctu}</td>
                          <td className="px-3 py-2 text-sm">{r.class_code}</td>
                          <td className="px-3 py-2 text-sm">{r.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Remarks */}
              {report.remarks && (
                <div className="bg-white border border-slate-200 rounded-md p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Remarks</h3>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{report.remarks}</p>
                </div>
              )}
            </>
          )}

          {/* General Photos */}
          {(report.photos || []).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>General Photos</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {report.photos.map(pid => (
                  <div key={pid} className="aspect-video bg-slate-100 rounded overflow-hidden border border-slate-200">
                    {photoUrls[pid] ? <img src={photoUrls[pid]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">Loading...</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Enhanced Photo Entries (new report types) */}
          {(report.photo_entries || []).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Photo Evidence</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {report.photo_entries.map((entry, i) => (
                  <div key={i} className="border border-slate-200 rounded overflow-hidden">
                    <div className="aspect-video bg-slate-100">
                      {photoUrls[entry.id] ? <img src={photoUrls[entry.id]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400">Loading...</div>}
                    </div>
                    <div className="p-2.5 bg-slate-50">
                      <p className="text-sm font-medium text-slate-700">{entry.caption || `Photo ${i+1}`}</p>
                      {entry.category && <p className="text-xs text-slate-500">{entry.category}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Damaged Shipment View */}
          {reportType === "damaged_shipment" && (
            <>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Shipment Details</h3>
                <FieldRow label="Shipper" value={report.shipper} />
                <FieldRow label="Consignee" value={report.consignee} />
                <FieldRow label="Carrier" value={report.carrier} />
                <FieldRow label="Vessel/Transport" value={report.vessel_details} />
                <FieldRow label="Container" value={report.container_number} />
                <FieldRow label="Seal" value={report.seal_number} />
                <FieldRow label="B/L / CMR" value={report.bill_of_lading_number} />
                <FieldRow label="Commodity" value={report.commodity_description} />
                <FieldRow label="Weight" value={report.total_gross_weight} />
                <FieldRow label="Packages" value={report.number_of_packages} />
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Damage Details</h3>
                <FieldRow label="Date Noticed" value={report.date_damage_noticed} />
                <FieldRow label="Location" value={report.location_damage_noticed} />
                <FieldRow label="Damage Type" value={(report.damage_type || "").replace(/_/g, " ")} />
                <FieldRow label="Severity" value={report.damage_severity} />
                <FieldRow label="Description" value={report.description_of_damage} />
                <FieldRow label="Suspected Cause" value={report.suspected_cause} />
                <FieldRow label="Action Taken" value={report.immediate_action_taken} />
                <FieldRow label="Customer Notified" value={report.customer_notified ? "Yes" : "No"} />
                <FieldRow label="Claim Reference" value={report.claim_reference_number} />
              </div>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Findings</h3>
                <FieldRow label="Findings" value={report.survey_findings} />
                <FieldRow label="Observations" value={report.observations} />
                <FieldRow label="Recommendation" value={report.recommendation} />
                <FieldRow label="Disclaimer" value={report.disclaimer} />
              </div>
            </>
          )}

          {/* Transloading View */}
          {reportType === "transloading" && (
            <>
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Operation Details</h3>
                <FieldRow label="Shipper" value={report.shipper} />
                <FieldRow label="Consignee" value={report.consignee} />
                <FieldRow label="Cargo" value={report.cargo_description} />
                <FieldRow label="Weight" value={report.total_gross_weight} />
                <FieldRow label="Origin Container" value={report.origin_container} />
                <FieldRow label="Destination Container" value={report.destination_container} />
                <FieldRow label="Origin Seal" value={report.origin_seal} />
                <FieldRow label="New Seal" value={report.new_seal} />
                <FieldRow label="Equipment" value={report.equipment_used} />
                <FieldRow label="Warehouse" value={report.warehouse_number} />
              </div>
              {Object.keys(report.checklist || {}).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-md p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Checklist</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {Object.entries(report.checklist).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-sm">
                        <span className={`w-5 h-5 rounded flex items-center justify-center text-xs font-bold ${v ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{v ? "Y" : "N"}</span>
                        <span className="text-slate-700">{k.replace(/_/g, " ")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(report.operation_timeline || []).length > 0 && (
                <div className="bg-white border border-slate-200 rounded-md p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Timeline</h3>
                  <table className="w-full"><thead><tr className="bg-slate-50 border-b"><th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Time</th><th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Activity</th><th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Person</th><th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Notes</th></tr></thead>
                    <tbody>{report.operation_timeline.map((r, i) => (<tr key={i} className="border-b border-slate-100"><td className="px-3 py-2 text-sm">{r.time}</td><td className="px-3 py-2 text-sm">{r.activity}</td><td className="px-3 py-2 text-sm">{r.responsible_person}</td><td className="px-3 py-2 text-sm">{r.notes}</td></tr>))}</tbody>
                  </table>
                </div>
              )}
              <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Findings</h3>
                <FieldRow label="Findings" value={report.survey_findings} />
                <FieldRow label="Observations" value={report.observations} />
                <FieldRow label="Exceptions" value={report.exceptions} />
                <FieldRow label="Recommendation" value={report.recommendation} />
                <FieldRow label="Disclaimer" value={report.disclaimer} />
              </div>
            </>
          )}
        </div>
      </main>

      {/* PDF Preview Dialog */}
      <Dialog open={showPdfPreview} onOpenChange={(open) => { if (!open) closePdfPreview(); }}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0" data-testid="pdf-preview-dialog">
          <DialogHeader className="p-4 border-b border-slate-200 flex flex-row items-center justify-between">
            <DialogTitle className="font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              PDF Preview — {report?.report_number}
            </DialogTitle>
            <div className="flex gap-2">
              <Button
                onClick={handleDownloadPdf}
                disabled={downloading}
                size="sm"
                className="bg-blue-900 hover:bg-blue-800 text-white gap-1.5"
                data-testid="preview-download-btn"
              >
                <FilePdf size={14} /> Download
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-slate-100">
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-3 border-blue-900 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-slate-500">Generating preview...</p>
                </div>
              </div>
            ) : pdfPreviewUrl ? (
              <iframe
                src={pdfPreviewUrl}
                className="w-full h-full border-0"
                title="PDF Preview"
                data-testid="pdf-preview-iframe"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
